import React from 'react';
import './styles.css';
import {appText} from '../../../assets/text/appText';
import TooltipQuestionMark from '../../TooltipQuestionMark';
import {Icon} from '../../Icon/Icon';

export type TextInputUITypes = 'standart' | 'pill' | 'box';

interface Props {
  /** Lets a separate <label htmlFor> name the input. */
  id?: string;
  type?: string;
  value: string;
  onFocusFunc?: () => void;
  onBlurFunc?: () => void;
  onChange: (value: string) => void;
  placeholder: string;
  heading?: string;
  subtitle?: string;
  className?: string;
  maxCharacters?: number;
  minCharacters?: number;
  fieldError?: boolean;
  disabled?: boolean;
  isMandatory?: boolean;
  uiType?: TextInputUITypes;
  noEdit?: boolean;
  helpTip?: string;
  onClear?: () => void;
  /** Says the field must be filled in, without the browser's own validation message. */
  ariaRequired?: boolean;
  /** Says the value is wrong, e.g. alongside fieldError. */
  ariaInvalid?: boolean;
  /** The id of text that describes the input, e.g. a hint under it. */
  ariaDescribedBy?: string;
  /** Off for text that is not prose, such as a code. */
  spellCheck?: boolean;
  /** The field's role when it is more than a textbox, e.g. "combobox" with a popup. */
  role?: React.AriaRole;
  /** For a field with a popup: what kind it is, whether it is open, and its id. */
  ariaHasPopup?: React.AriaAttributes['aria-haspopup'];
  ariaExpanded?: boolean;
  ariaControls?: string;
}

const TextInput = React.memo(
  ({
    id,
    type,
    value,
    onChange,
    placeholder,
    heading,
    className,
    maxCharacters,
    fieldError,
    disabled,
    isMandatory,
    uiType,
    minCharacters,
    onFocusFunc,
    onBlurFunc,
    noEdit,
    subtitle,
    helpTip,
    onClear,
    ariaRequired,
    ariaInvalid,
    ariaDescribedBy,
    spellCheck,
    role,
    ariaHasPopup,
    ariaExpanded,
    ariaControls,
  }: Props) => {
    const getRootClass = () => {
      switch (uiType) {
        case 'pill':
          return 'TextInputPill';
        case 'box':
          return 'TextInputBox';
        default:
          return 'TextInputStandart';
      }
    };

    return (
      <div className={className}>
        {heading && (
          <div className="d-flex flex-row align-items-center">
            <p className="TextInputTitle">
              {heading}
              {isMandatory ? <span className="MandatoryIndicator">*</span> : ''}
            </p>
            {helpTip && <TooltipQuestionMark text={helpTip} />}
          </div>
        )}
        {subtitle && <p className="TextInputSubtitle">{subtitle}</p>}
        <div
          className={`TextInputContainer ${
            onClear ? 'TextInputClearable' : ''
          }`}>
          <input
            id={id}
            type={type}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            onFocus={onFocusFunc}
            onBlur={onBlurFunc}
            className={`${getRootClass()} ${fieldError ? 'FieldError' : ''} ${
              disabled ? 'DisabledInput' : ''
            }`}
            maxLength={maxCharacters}
            disabled={disabled || noEdit}
            aria-required={ariaRequired || undefined}
            aria-invalid={ariaInvalid || undefined}
            aria-describedby={ariaDescribedBy}
            spellCheck={spellCheck}
            role={role}
            aria-haspopup={ariaHasPopup}
            aria-expanded={ariaExpanded}
            aria-controls={ariaControls}
          />
          {onClear && value && !disabled && !noEdit && (
            <button
              type="button"
              className="TextInputClearButton"
              aria-label="Clear"
              onClick={onClear}>
              <Icon name="clear.svg" />
            </button>
          )}
        </div>
        <div className="TextInputCharactersCountContainer">
          {minCharacters ? (
            <div className="TextInputMaxCharacters">
              <span>{appText.minimumCharacters(minCharacters)}</span>
            </div>
          ) : (
            <div />
          )}
          {maxCharacters ? (
            <div className="TextInputMaxCharacters">
              <span>
                {value.length} / {maxCharacters}
              </span>
            </div>
          ) : (
            <div />
          )}
        </div>
      </div>
    );
  },
);

export default TextInput;
